import { useEffect, useRef, useState } from 'react';

import { VInput } from '../../VInput';
import { VStack } from '../../VStack';
import type { VModalContextValue } from './type';

type VPromptModalMessageProps = Pick<
  Parameters<VModalContextValue['openPrompt']>[0],
  'promptMessage' | 'inputProps'
> & {
  onChangePromptValue: (value: string) => void;
  validationError?: Nullish<Error>;
};

const VPromptModalMessage = ({
  inputProps,
  promptMessage,
  validationError,
  onChangePromptValue,
}: React.PropsWithChildren<VPromptModalMessageProps>) => {
  const [innerInputProps, setInnerInputProps] = useState(inputProps ?? {});
  const [promptValue, setPromptValue] = useState(
    innerInputProps.defaultValue ?? '',
  );
  const [errorMessage, setErrorMessage] = useState<string | undefined>(
    validationError?.message,
  );
  const inputRef = useRef<HTMLInputElement>(null);

  const onChange: React.ChangeEventHandler<HTMLInputElement> = e => {
    setPromptValue(e.target.value);

    if (errorMessage) {
      setErrorMessage(undefined);
    }
  };

  useEffect(() => {
    setInnerInputProps(inputProps ?? {});
  }, [inputProps]);

  useEffect(() => {
    setErrorMessage(validationError?.message);
  }, [validationError]);

  useEffect(() => {
    if (promptValue === inputProps?.defaultValue) return;

    onChangePromptValue(promptValue);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputProps?.defaultValue, promptValue]);

  return (
    <VStack gap={0.5}>
      {typeof promptMessage === 'string' ? (
        <div>{promptMessage}</div>
      ) : typeof promptMessage === 'function' ? (
        promptMessage()
      ) : null}

      <VInput
        ref={inputRef}
        autoFocus
        fluid
        label="input"
        defaultValue={promptValue}
        valid={errorMessage ? false : undefined}
        onChange={onChange}
        onKeyDown={e => {
          if (e.key === 'Enter') {
            e.preventDefault();

            setPromptValue((e.target as HTMLInputElement).value);
          }
        }}
        {...innerInputProps}
        comment={errorMessage ?? innerInputProps.comment}
      />
    </VStack>
  );
};

VPromptModalMessage.displayName = 'VPromptModalMessage';

export default VPromptModalMessage;
